/**
 * プロダクト管理用リポジトリ（API 一本化）
 */
import { adminApiFetch } from '../utils/adminApi.js';

class AdminProductsRepository {
  static instance = null;

  static getInstance() {
    if (!AdminProductsRepository.instance) {
      AdminProductsRepository.instance = new AdminProductsRepository();
    }
    return AdminProductsRepository.instance;
  }

  async getAllProducts() {
    try {
      const data = await adminApiFetch('/api/admin/products');
      return data.products || [];
    } catch (error) {
      console.error('製品取得エラー:', error);
      throw error;
    }
  }

  async createProduct(productData) {
    try {
      const data = await adminApiFetch('/api/admin/create-product', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(productData)
      });

      return {
        id: data.productId,
        licenseName: data.licenseName,
        licenseKeys: data.licenseKeys,
        licenseSlot: data.licenseSlots,
        ...productData
      };
    } catch (error) {
      console.error('製品作成エラー:', error);
      throw error;
    }
  }

  async updateProduct(productId, productData) {
    try {
      const data = await adminApiFetch('/api/admin/update-product', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId, ...productData })
      });

      return {
        id: productId,
        licenseName: data.licenseName,
        licenseKeys: data.licenseKeys,
        ...productData
      };
    } catch (error) {
      console.error('製品更新エラー:', error);
      throw error;
    }
  }

  async deleteProduct(productId, reason) {
    try {
      await adminApiFetch('/api/admin/delete-product', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId, reason })
      });
      return true;
    } catch (error) {
      console.error('製品削除エラー:', error);
      throw error;
    }
  }

  async updateDisplayOrder(productId, newOrder) {
    return this.updateMultipleDisplayOrders([{ id: productId, displayOrder: newOrder }]);
  }

  async updateMultipleDisplayOrders(orderUpdates) {
    try {
      await adminApiFetch('/api/admin/products/display-order', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: orderUpdates })
      });
      return true;
    } catch (error) {
      console.error('一括表示順更新エラー:', error);
      throw error;
    }
  }

  async createStripeProduct(productId) {
    try {
      return await adminApiFetch('/api/admin/create-stripe-product', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId })
      });
    } catch (error) {
      console.error('Stripe製品作成エラー:', error);
      throw error;
    }
  }

  async updateStripeProduct(productId, { existingSubscriberPolicy, monthlyPrice } = {}) {
    try {
      const body = { productId, existingSubscriberPolicy };
      if (monthlyPrice !== undefined && monthlyPrice !== null) {
        body.monthlyPrice = monthlyPrice;
      }
      return await adminApiFetch('/api/admin/update-stripe-product', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
    } catch (error) {
      console.error('Stripe製品更新エラー:', error);
      throw error;
    }
  }

  async getStripePriceSyncPreview(productId, { monthlyPrice } = {}) {
    try {
      const query = monthlyPrice !== undefined && monthlyPrice !== null
        ? `?monthlyPrice=${encodeURIComponent(monthlyPrice)}`
        : '';
      return await adminApiFetch(`/api/admin/products/${productId}/stripe-price-sync-preview${query}`);
    } catch (error) {
      console.error('Stripe価格プレビュー取得エラー:', error);
      throw error;
    }
  }

  /**
   * 製品からのライセンスキー改名は非対応。RegistryLicenseKeyEditor（license-registry/rename）を使用すること。
   * 登録済みキーへの差し替えは swapProductLicenseKey を使用。
   */
  async renameProductLicenseKey(productId, oldKey, newKey) {
    return adminApiFetch('/api/admin/product-license-keys/rename', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productId, oldKey, newKey })
    });
  }

  async addProductLicenseKey(productId, key) {
    return adminApiFetch('/api/admin/product-license-keys/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productId, key })
    });
  }

  async swapProductLicenseKey(productId, oldKey, newKey) {
    return adminApiFetch('/api/admin/product-license-keys/swap', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productId, oldKey, newKey })
    });
  }

  async removeProductLicenseKey(productId, key, purgeAccounts = false) {
    return adminApiFetch('/api/admin/product-license-keys/remove', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productId, key, purgeAccounts })
    });
  }

  async reorderProductLicenseKeys(productId, keys) {
    return adminApiFetch('/api/admin/product-license-keys/reorder', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productId, keys })
    });
  }
}

export default AdminProductsRepository;
